<template>
  <div class="mt-6 sm:mt-8">
    <div class="flex justify-between items-center mb-4">
      <h3 class="text-lg sm:text-xl font-semibold ">Message History</h3>
      <Button
        variant="ghost"
        size="icon"
        :disabled="loading || refreshing"
        @click="handleRefresh"
        class="h-8 w-8 min-h-[44px] min-w-[44px] "
      >
        <v-icon
          :icon="'mdi-refresh'"
          class="h-4 w-4"
          :class="{ 
            'animate-spin': refreshing || loading
          }"
        ></v-icon>
      </Button>
    </div>
    
    <div v-if="loading" class="flex justify-center p-4">
      <v-icon icon="mdi-loading" class="h-8 w-8 animate-spin text-primary "></v-icon>
    </div>
    <div v-else-if="messages.length === 0" class="p-6 text-center text-muted-foreground bg-card/20 rounded-lg">
      No messages yet.
    </div>
    <div v-else class="space-y-2 sm:space-y-3">
      <div
        v-for="(msg, index) in messages" 
        :key="index"
        class="p-3 sm:p-4 rounded-lg border" 
        :class="msg.source === 'USER' ? 
          'bg-blue-50  border-blue-200' : 
          'bg-gray-50 border-gray-200'"
      >
        <div class="flex items-center gap-2 mb-1">
          <div class="rounded-full p-1 min-h-[24px] min-w-[24px] flex items-center justify-center" 
               :class="msg.source === 'USER' ? 
                 'bg-blue-500/20 ' : 
                 'bg-green-500/20 '">
            <v-icon
              :icon="msg.source === 'USER' ? 'mdi-account' : 'mdi-duck'"
              class="h-4 w-4"
              :color="getIconColor(msg.source)"
            ></v-icon>
          </div>
          <span class="font-medium text-sm text-slate-800 ">
            {{ msg.source === 'USER' ? 'Cruiser' : (msg.senderInfo || 'You') }}
          </span>
          <span class="text-xs text-gray-500  ml-auto">
            {{ formatDate(msg.timestamp) }}
          </span>
        </div>
        <p class="pl-6 text-sm sm:text-base text-slate-700  break-words">
          {{ msg.content }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Button } from '@/components/ui/button';
import type { Message } from '@/interfaces/message';

defineProps<{
  messages: Message[];
  loading: boolean;
}>();

const refreshing = ref(false);
const emit = defineEmits(['refresh']);

// Format date in a readable format
const formatDate = (timestamp: string | number) => {
  return new Date(timestamp).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getIconColor = (source: string) => {
  return source === 'USER' ? 'blue' : 'green';
};

// Handle refresh click with animation
const handleRefresh = () => {
  refreshing.value = true;
  emit('refresh');
  
  // Reset refreshing state after animation completes
  setTimeout(() => {
    refreshing.value = false;
  }, 1000);
};
</script>